import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClients,
  createClient,
  updateClient,
  getClient,
  addClientMember,
  removeClientMember,
  addClientGroup,
  removeClientGroup,
  getClientReportSummary,
} from '../services/clientService';
import { getProjects, createProject } from '../services/projectService';
import { listUsers } from '../services/userService';
import { useSpaces } from '../context/SpaceContext';
import './ClientsPage.css';

const ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer', 'accounting'];

export default function ClientsPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { spaces = [] } = useSpaces() || {};

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    notes: '',
    visibility: 'private',
  });
  const [saving, setSaving] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [savingProject, setSavingProject] = useState(false);

  const [editingClient, setEditingClient] = useState(false);
  const [editClientData, setEditClientData] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [groupId, setGroupId] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1024;
  });

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    listUsers()
      .then((res) => setAllUsers(res.data.users || []))
      .catch((err) => console.error('Failed to load users:', err));
  }, []);

  const refreshSelectedClient = async (id) => {
    const res = await getClient(id);
    const loaded = res.data.client;
    setSelectedClient(loaded);
    setEditClientData({ ...loaded });
    return loaded;
  };

  const loadClientSummary = async (id) => {
    try {
      const res = await getClientReportSummary(id);
      setSummary(res.data.summary || null);
      setSummaryError('');
    } catch (err) {
      setSummary(null);
      setSummaryError(err?.response?.data?.message || 'No reporting access for this client summary.');
    }
  };

  useEffect(() => {
    setLoading(true);
    getClients()
      .then(async (res) => {
        const list = res.data.clients || [];
        setClients(list);
        if (clientId) {
          const found = list.find((c) => c._id === clientId);
          if (found) {
            await openClient(found);
          }
        }
      })
      .catch((err) => console.error('Failed to load clients:', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const openClient = async (client) => {
    setSelectedClient(client);
    setEditingClient(false);
    setEditClientData({ ...client });
    setProjectsLoading(true);
    getProjects({ clientId: client._id })
      .then((res) => setClientProjects(res.data.projects || []))
      .catch((err) => console.error('Failed to load projects:', err))
      .finally(() => setProjectsLoading(false));
    navigate(`/clients/${client._id}`, { replace: true });

    try {
      await refreshSelectedClient(client._id);
      await loadClientSummary(client._id);
    } catch (err) {
      console.error('Failed to load client details:', err);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.name.trim()) return;
    setSaving(true);
    try {
      const res = await createClient(newClient);
      setClients((prev) => [res.data.client, ...prev]);
      setNewClient({ name: '', contactName: '', email: '', phone: '', notes: '', visibility: 'private' });
      setShowNewClient(false);
      await openClient(res.data.client);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    setSaving(true);
    try {
      const res = await updateClient(selectedClient._id, editClientData);
      const updated = res.data.client;
      setClients((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
      await refreshSelectedClient(updated._id);
      await loadClientSummary(updated._id);
      setEditingClient(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim() || !selectedClient) return;
    setSavingProject(true);
    try {
      const res = await createProject({ ...newProject, client: selectedClient._id });
      setClientProjects((prev) => [res.data.project, ...prev]);
      setNewProject({ name: '', description: '' });
      setShowNewProject(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setSavingProject(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedClient || !memberUserId) return;
    try {
      await addClientMember(selectedClient._id, memberUserId, memberRole);
      setMemberUserId('');
      setMemberRole('member');
      await refreshSelectedClient(selectedClient._id);
      await loadClientSummary(selectedClient._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedClient) return;
    try {
      await removeClientMember(selectedClient._id, userId);
      await refreshSelectedClient(selectedClient._id);
      await loadClientSummary(selectedClient._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    if (!selectedClient || !groupId) return;
    try {
      await addClientGroup(selectedClient._id, groupId);
      setGroupId('');
      await refreshSelectedClient(selectedClient._id);
      await loadClientSummary(selectedClient._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add group');
    }
  };

  const handleRemoveGroup = async (id) => {
    if (!selectedClient) return;
    try {
      await removeClientGroup(selectedClient._id, id);
      await refreshSelectedClient(selectedClient._id);
      await loadClientSummary(selectedClient._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove group');
    }
  };

  const visibleMembers = selectedClient?.members || [];
  const linkedGroups = selectedClient?.groupIds || [];

  return (
    <div className={`clients-page ${isMobileViewport && selectedClient ? 'mobile-detail' : ''}`}>
      <div className="clients-sidebar">
        <div className="clients-sidebar-header">
          <h2 className="clients-sidebar-title">Clients</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewClient((v) => !v)}>
            + New
          </button>
        </div>

        {showNewClient && (
          <form onSubmit={handleCreateClient} className="client-new-form">
            <input
              autoFocus
              className="form-input"
              placeholder="Client name *"
              value={newClient.name}
              onChange={(e) => setNewClient((p) => ({ ...p, name: e.target.value }))}
            />
            <input
              className="form-input"
              placeholder="Contact name"
              value={newClient.contactName}
              onChange={(e) => setNewClient((p) => ({ ...p, contactName: e.target.value }))}
            />
            <input
              className="form-input"
              placeholder="Email"
              type="email"
              value={newClient.email}
              onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))}
            />
            <select
              className="form-select"
              value={newClient.visibility}
              onChange={(e) => setNewClient((p) => ({ ...p, visibility: e.target.value }))}
            >
              <option value="private">Private</option>
              <option value="shared">Shared</option>
              <option value="org">Org-wide</option>
            </select>
            <div className="form-row">
              <button type="submit" className="btn btn-primary btn-sm" disabled={!newClient.name.trim() || saving}>
                {saving ? 'Saving...' : 'Create'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowNewClient(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="panel-muted" style={{ padding: '16px' }}>Loading clients...</p>
        ) : clients.length === 0 ? (
          <p className="panel-muted" style={{ padding: '16px' }}>No clients yet. Create one to get started.</p>
        ) : (
          <ul className="clients-list">
            {clients.filter((c) => !c.isArchived).map((client) => (
              <li
                key={client._id}
                className={`clients-list-item ${selectedClient?._id === client._id ? 'active' : ''}`}
                onClick={() => openClient(client)}
              >
                <div className="client-list-avatar">{client.name[0].toUpperCase()}</div>
                <div className="client-list-info">
                  <span className="client-list-name">{client.name}</span>
                  {client.contactName && <span className="client-list-contact">{client.contactName}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedClient ? (
        <div className="client-detail">
          <div className="client-detail-header">
            {isMobileViewport && (
              <button
                className="btn btn-ghost btn-sm client-mobile-back"
                onClick={() => {
                  setSelectedClient(null);
                  navigate('/clients', { replace: true });
                }}
              >
                {'<-'} Clients
              </button>
            )}
            <div className="client-detail-avatar">{selectedClient.name[0].toUpperCase()}</div>
            <div className="client-detail-title">
              <h1 className="client-detail-name">{selectedClient.name}</h1>
              {selectedClient.contactName && <p className="client-detail-contact">{selectedClient.contactName}</p>}
            </div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setEditingClient((v) => !v);
                setEditClientData({ ...selectedClient });
              }}
            >
              {editingClient ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {editingClient ? (
            <form onSubmit={handleUpdateClient} className="client-edit-form">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  className="form-input"
                  value={editClientData.name || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  className="form-input"
                  value={editClientData.contactName || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, contactName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={editClientData.email || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  className="form-input"
                  value={editClientData.phone || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  className="form-input"
                  value={editClientData.address || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={editClientData.notes || ''}
                  onChange={(e) => setEditClientData((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <select
                  className="form-select"
                  value={editClientData.visibility || 'private'}
                  onChange={(e) => setEditClientData((p) => ({ ...p, visibility: e.target.value }))}
                >
                  <option value="private">Private</option>
                  <option value="shared">Shared</option>
                  <option value="org">Org-wide</option>
                </select>
              </div>
              <div className="form-row">
                <button type="submit" className="btn btn-primary" disabled={saving || !editClientData.name?.trim()}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditingClient(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="client-info-grid">
              <div className="client-info-item">
                <span className="client-info-label">Visibility</span>
                <span className="client-info-value">{selectedClient.visibility || 'private'}</span>
              </div>
              {selectedClient.email && (
                <div className="client-info-item">
                  <span className="client-info-label">Email</span>
                  <a href={`mailto:${selectedClient.email}`} className="client-info-value link">{selectedClient.email}</a>
                </div>
              )}
              {selectedClient.phone && (
                <div className="client-info-item">
                  <span className="client-info-label">Phone</span>
                  <span className="client-info-value">{selectedClient.phone}</span>
                </div>
              )}
              {selectedClient.address && (
                <div className="client-info-item">
                  <span className="client-info-label">Address</span>
                  <span className="client-info-value">{selectedClient.address}</span>
                </div>
              )}
              {selectedClient.notes && (
                <div className="client-info-item client-notes">
                  <span className="client-info-label">Notes</span>
                  <span className="client-info-value">{selectedClient.notes}</span>
                </div>
              )}
            </div>
          )}

          <div className="client-projects-section">
            <div className="client-projects-header">
              <h3 className="client-projects-title">Access and Reporting</h3>
            </div>

            <div className="form-row" style={{ alignItems: 'flex-start' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Members</label>
                {visibleMembers.length === 0 ? (
                  <p className="panel-muted">No direct members.</p>
                ) : (
                  <ul className="project-spaces-list">
                    {visibleMembers.map((m) => (
                      <li key={String(m.user?._id || m.user)} className="project-space-item">
                        <span className="project-space-name">{m.user?.name || m.user} ({m.role})</span>
                        <button className="btn-icon-danger" onClick={() => handleRemoveMember(m.user?._id || m.user)} title="Remove">X</button>
                      </li>
                    ))}
                  </ul>
                )}
                <form onSubmit={handleAddMember} className="form-row" style={{ marginTop: 8 }}>
                  <select className="form-select" value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)}>
                    <option value="">Select user</option>
                    {allUsers.map((u) => (
                      <option key={u._id} value={u._id}>{u.name}</option>
                    ))}
                  </select>
                  <select className="form-select" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button type="submit" className="btn btn-ghost btn-sm" disabled={!memberUserId}>Add</button>
                </form>
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Groups (Spaces)</label>
                {linkedGroups.length === 0 ? (
                  <p className="panel-muted">No linked groups.</p>
                ) : (
                  <ul className="project-spaces-list">
                    {linkedGroups.map((g) => (
                      <li key={String(g._id || g)} className="project-space-item">
                        <span className="project-space-name">{g.name || g}</span>
                        <button className="btn-icon-danger" onClick={() => handleRemoveGroup(g._id || g)} title="Remove">X</button>
                      </li>
                    ))}
                  </ul>
                )}
                <form onSubmit={handleAddGroup} className="form-row" style={{ marginTop: 8 }}>
                  <select className="form-select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                    <option value="">Select group</option>
                    {spaces.map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                  <button type="submit" className="btn btn-ghost btn-sm" disabled={!groupId}>Add</button>
                </form>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <h4 className="project-section-heading">Accounting Summary</h4>
              {summaryError && <p className="panel-muted">{summaryError}</p>}
              {!summary && !summaryError && <p className="panel-muted">No reporting data yet.</p>}
              {summary && (
                <div>
                  <p className="panel-muted">
                    Users: {summary.userCount} | Hours: {(summary.totals?.hours || 0).toFixed(2)} | Billable: {(summary.totals?.billableHours || 0).toFixed(2)} | KM: {(summary.totals?.km || 0).toFixed(1)}
                  </p>
                  <table className="time-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Hours</th>
                        <th>Billable</th>
                        <th>KM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.users || []).map((row) => (
                        <tr key={row.userId}>
                          <td>{row.userName}</td>
                          <td>{(row.totalHours || 0).toFixed(2)}</td>
                          <td>{(row.billableHours || 0).toFixed(2)}</td>
                          <td>{(row.totalKm || 0).toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="client-projects-section">
            <div className="client-projects-header">
              <h3 className="client-projects-title">Projects</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowNewProject((v) => !v)}>
                + New Project
              </button>
            </div>

            {showNewProject && (
              <form onSubmit={handleCreateProject} className="project-new-form">
                <input
                  autoFocus
                  className="form-input"
                  placeholder="Project name *"
                  value={newProject.name}
                  onChange={(e) => setNewProject((p) => ({ ...p, name: e.target.value }))}
                />
                <input
                  className="form-input"
                  placeholder="Description"
                  value={newProject.description}
                  onChange={(e) => setNewProject((p) => ({ ...p, description: e.target.value }))}
                />
                <div className="form-row">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!newProject.name.trim() || savingProject}>
                    {savingProject ? 'Creating...' : 'Create'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowNewProject(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {projectsLoading ? (
              <p className="panel-muted">Loading projects...</p>
            ) : clientProjects.length === 0 ? (
              <p className="panel-muted">No projects for this client yet.</p>
            ) : (
              <ul className="client-projects-list">
                {clientProjects.map((project) => (
                  <li
                    key={project._id}
                    className="client-project-item"
                    onClick={() => navigate(`/projects/${project._id}`)}
                  >
                    <div className="project-item-info">
                      <span className="project-item-name">{project.name}</span>
                      {project.description && <span className="project-item-desc">{project.description}</span>}
                    </div>
                    <span className={`project-status-badge status-${project.status}`}>{project.status}</span>
                    <span className="project-item-arrow">{String.fromCharCode(8250)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="client-detail client-detail-empty">
          <p className="panel-muted">Select a client to view details.</p>
        </div>
      )}
    </div>
  );
}
